import {
  AVAILABLE_REQUEST_MIDDLEWARE_ORDER,
  fetchRequestMiddleware,
} from './fetch/request_middleware/fetchRequestMiddleware'
import { ExtendedRequestInfo, ExtendedRequestInfoSafe } from './fetch/types'

const nativeFetch = globalThis.fetch

const GZIP_BASE64_PREFIX = 'H4sI'

async function decodeBase64Gzip(encoded: string): Promise<string> {
  const bytes = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0))
  const ds = new DecompressionStream('gzip')
  const writer = ds.writable.getWriter()
  writer.write(bytes)
  writer.close()
  return new Response(ds.readable).text()
}

function decodeBase64(encoded: string): string {
  return atob(encoded)
}

interface OversizeWrapper {
  statusCode: number
  headers: Record<string, string>
  body: string
  bodyEncoding?: 'base64+gzip' | 'base64'
}

/**
 * Decodes the wrapper body based on the explicit bodyEncoding flag set by the
 * backend. Falls back to gzip prefix sniffing for wrappers written before the
 * flag was introduced.
 */
async function decodeWrapperBody(wrapper: OversizeWrapper): Promise<string> {
  const { body, bodyEncoding } = wrapper

  if (bodyEncoding === 'base64+gzip') {
    if (typeof DecompressionStream !== 'undefined') {
      return decodeBase64Gzip(body)
    }
    return body
  }

  if (bodyEncoding === 'base64') {
    return decodeBase64(body)
  }

  if (
    !bodyEncoding &&
    typeof body === 'string' &&
    body.startsWith(GZIP_BASE64_PREFIX) &&
    typeof DecompressionStream !== 'undefined'
  ) {
    try {
      return await decodeBase64Gzip(body)
    } catch {
      return body
    }
  }

  return body
}

/**
 * When the Lambda response exceeds the payload limit, the custom handler
 * stores the full response (headers + body) in S3 and returns a small
 * JSON envelope flagged with X-Oversize-Response. This function detects
 * the flag, fetches the wrapper from S3, and reconstructs the original
 * Response so callers see the correct status, headers, and body.
 *
 * The wrapper may include a bodyEncoding field ("base64+gzip" or "base64")
 * that tells the frontend how to decode the body. Older wrappers without
 * the field fall back to gzip magic-byte prefix detection.
 */
async function followOversizeResponse(response: Response): Promise<Response> {
  if (response.headers.get('X-Oversize-Response') !== 'true') {
    return response
  }

  const envelope = await response.json()
  const s3Response = await nativeFetch(envelope.url)
  const wrapper: OversizeWrapper = await s3Response.json()
  const body = await decodeWrapperBody(wrapper)

  const headers = { ...wrapper.headers }
  if (wrapper.bodyEncoding) {
    delete headers['Content-Encoding']
  }

  return new Response(body, {
    status: wrapper.statusCode,
    statusText: 'OK',
    headers,
  })
}

/**
 * When the backend returns a cached file URL instead of a 303 redirect
 * (to avoid CORS preflight failures from custom headers leaking to S3),
 * it sends a JSON envelope flagged with X-Cached-Redirect. This function
 * detects the flag and fetches the file directly from S3 using nativeFetch
 * (no custom headers), returning the S3 response to the caller transparently.
 */
async function followCachedRedirect(response: Response): Promise<Response> {
  if (response.headers.get('X-Cached-Redirect') !== 'true') {
    return response
  }
  const envelope = await response.json()
  return nativeFetch(envelope.url)
}

globalThis.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await nativeFetch(input, init)
  const afterOversize = await followOversizeResponse(response)
  return followCachedRedirect(afterOversize)
}

/**
 * Returns the parsed oversize envelope if the raw text matches the shape
 * { oversize: true, url: "<string>" }, otherwise null.
 * Checking the body avoids reliance on Access-Control-Expose-Headers which
 * can be unreliable for XHR on cross-origin responses.
 */
function parseOversizeEnvelope(raw: string): { oversize: true; url: string } | null {
  try {
    const data = JSON.parse(raw)
    if (data && data.oversize === true && typeof data.url === 'string') {
      return data
    }
  } catch {
    // Not JSON or not an oversize envelope
  }
  return null
}

// Patch XMLHttpRequest for jQuery $.ajax() calls (studio code).
// jQuery sets xhr.onload (3.x) or xhr.onreadystatechange (1.x/2.x) before
// calling send(), so wrapping those handlers here intercepts the response
// before jQuery's internal deferred resolution processes the envelope.
const origXHRSend = XMLHttpRequest.prototype.send
XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
  const xhr = this

  function replaceXHRResponse(wrapper: { body: string; statusCode: number }) {
    Object.defineProperty(xhr, 'responseText', { value: wrapper.body, configurable: true })
    Object.defineProperty(xhr, 'response', { value: wrapper.body, configurable: true })
    Object.defineProperty(xhr, 'status', { value: wrapper.statusCode, configurable: true })
  }

  function handleOversizeXHR(origHandler: (this: XMLHttpRequest, ev: any) => any, ev: any) {
    if (xhr.responseType !== '' && xhr.responseType !== 'text') {
      // Skip oversize response handling for non-text/binary responses
      origHandler.call(xhr, ev)
      return
    }
    const envelope = parseOversizeEnvelope(xhr.responseText)
    if (!envelope) {
      origHandler.call(xhr, ev)
      return
    }
    nativeFetch(envelope.url)
      .then((r) => r.json())
      .then(async (wrapper: OversizeWrapper) => {
        wrapper.body = await decodeWrapperBody(wrapper)
        replaceXHRResponse(wrapper)
        origHandler.call(xhr, ev)
      })
      .catch(() => origHandler.call(xhr, ev))
  }

  if (this.onload) {
    const origOnLoad = this.onload
    this.onload = function (ev) {
      handleOversizeXHR(origOnLoad, ev)
    }
  }

  if (this.onreadystatechange) {
    const origOnRSC = this.onreadystatechange
    this.onreadystatechange = function (ev) {
      if (xhr.readyState !== 4) {
        return origOnRSC.call(xhr, ev)
      }
      handleOversizeXHR(origOnRSC, ev)
    }
  }

  origXHRSend.call(this, body)
}

function processRequestInfo(input: RequestInfo | URL, init?: ExtendedRequestInfo): ExtendedRequestInfoSafe {
  if (!init) init = {}
  let headers: Headers = new Headers(init.headers)

  if (input instanceof Request) {
    input.headers.forEach((value, key) => headers.set(key, value))
  }

  return {
    ...init,
    headers,
  }
}

/**
 * Fetch that runs the same request middleware as fetchRequest (content-type, auth, app version, etc.).
 * Same signature as native fetch. Use for raw fetch-style calls or via fetchRequest.
 */
export async function osFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const options = processRequestInfo(input, init as ExtendedRequestInfo)

  if (fetchRequestMiddleware.size === 0) {
    console.warn('fetch middleware not initialised yet')
  }

  let request: Request = new Request(input, options)

  for (const key of AVAILABLE_REQUEST_MIDDLEWARE_ORDER) {
    const middleware = fetchRequestMiddleware.get(key)
    if (middleware) {
      await middleware(request, options)
    }
  }

  // Need to regenerate the request object after middleware has run
  request = new Request(request, options)
  const response = await nativeFetch(request)
  const afterOversize = await followOversizeResponse(response)
  return followCachedRedirect(afterOversize)
}
